import {
    ReactElement,
    useState,
    useRef,
    useLayoutEffect,
    useCallback,
    KeyboardEventHandler,
    SyntheticEvent,
    useMemo,
} from 'react';

import { StaticDataFetcherItem } from '@hh.ru/magritte-common-data-provider';
import {
    findNextFocusableElement,
    keyboardKeys,
    keyboardMatch,
    keyboardMatches,
} from '@hh.ru/magritte-common-keyboard';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { BaseInput } from '@hh.ru/magritte-ui-input/BaseInput';
import { SelectBottomSheet } from '@hh.ru/magritte-ui-select/SelectBottomSheet';
import { SelectDrop } from '@hh.ru/magritte-ui-select/SelectDrop';
import {
    SelectProps,
    TriggerPropsWithRef,
    FocusStateType,
    SetSearchValue,
    OptionsCount,
} from '@hh.ru/magritte-ui-select/types';
import { useSelectOptionsList } from '@hh.ru/magritte-ui-select/useSelectOptionsList';
import { useSelectSearch } from '@hh.ru/magritte-ui-select/useSelectSearch';
import { useSelectState } from '@hh.ru/magritte-ui-select/useSelectState';
import { Trigger } from '@hh.ru/magritte-ui-trigger';

const SelectTriggerDefault = (triggerProps: TriggerPropsWithRef): ReactElement => {
    return <Trigger {...triggerProps} />;
};

const focusOptionsList = (event: SyntheticEvent, drop: HTMLElement | null | undefined, direction: 'up' | 'down') => {
    if (drop) {
        const nextFocusableElement = findNextFocusableElement(drop, null, direction, 'list');
        if (nextFocusableElement !== null) {
            event.preventDefault();
            nextFocusableElement.focus();
        }
    }
};

interface SearchInputState {
    useSearchInput: boolean;
    emitEvents: boolean;
}

const SEARCH_INPUT_STATE: SearchInputState = {
    useSearchInput: false,
    emitEvents: false,
};

export const Select = <MultipleType extends boolean, DataType extends StaticDataFetcherItem>({
    type,
    multiple,
    value: _value,
    maxSelectedOptions,
    loading,
    error,
    searchable: _searchable,
    bottomSheetHeight = 'full-screen',
    onChange: onChangeHandler,
    renderTrigger = SelectTriggerDefault,
    triggerProps,
    renderMobileItem,
    renderDesktopItem,
    name,
    maxWidth,
    maxHeight = 460,
    useInitialValuesOnClearAction,
    renderAlertOnExitWithoutSaving,
    renderButtonsContainer,
    renderHeader,
    headerTitle: _headerTitle,
    clearButton,
    applyChangesButton,
    forceDropVisibleWithEmptyOptions,
    plain = false,
    widthEqualToActivator = true,
    renderContentBefore,
    onBlur,
    onFocus,
    onDropOpen,
    onDropClose,
    onBottomSheetClose,
    onBottomSheetOpen,
    onSearchSubmit,
    onApply,
    onClear,
    onSelectOption,
    pickerRef,
    dataProvider,
    emptyOptionsHint,
    emptyOptionsSearchHint,
    onOptionsChange,
    innerErrorTrls,
    optionDataQa,
}: SelectProps<MultipleType, DataType>): ReactElement => {
    const { isMobile } = useBreakpoint();
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchInputState, setSearchInputState] = useState<SearchInputState>(SEARCH_INPUT_STATE);
    const [forcedState, setForcedState] = useState<FocusStateType>(null);
    const dropRef = useRef<HTMLDivElement>(null);
    const useDefaultTrigger = renderTrigger === SelectTriggerDefault;

    const { activatorRef, optionsListRef, onOptionsListClose, toggleListVisibility, showOptions } =
        useSelectOptionsList(isMobile);
    const prevVisibleRef = useRef<boolean>(showOptions);
    const hasChangesButton = multiple && Boolean(applyChangesButton);
    const {
        values,
        onChange,
        clearSelectedValues,
        setInitialValues,
        setSelectedValues,
        setInitialValuesWithoutOnChange,
        initialValuesOnOpening,
    } = useSelectState<DataType, MultipleType>(
        !!multiple,
        _value || [],
        showOptions,
        hasChangesButton,
        onChangeHandler,
        onSelectOption
    );
    const valuesArray = useMemo(
        // eslint-disable-next-line no-nested-ternary
        () => (Array.isArray(values) ? values : values ? ([values] as DataType[]) : []),
        [values]
    );
    const { optionList, internalLoading, searchValue, setSearchValue, searchInProgress, reload, internalError } =
        useSelectSearch<DataType>(showOptions, valuesArray, dataProvider, onOptionsChange, multiple);

    const selectedLabels = useMemo(
        () =>
            // eslint-disable-next-line no-nested-ternary
            Array.isArray(_value)
                ? _value.map((item) => item.text ?? item.value)
                : _value
                  ? [_value?.text ?? _value.value]
                  : [],
        [_value]
    );

    const optionsListRefCallback = useMultipleRefs(optionsListRef, dropRef, pickerRef);
    const showedByFocus = useRef<boolean>(false);
    const isLimitExceeded =
        maxSelectedOptions !== undefined && Array.isArray(values) && values.length >= maxSelectedOptions;
    const { size, stretched, label: triggerLabel, disabled } = triggerProps;
    const sizeProp = size === 'large' ? ({ size: 'large' } as const) : ({ size: 'medium' } as const);
    const inputWrapperStyles = stretched ? { with: '100%' } : { maxWidth: activatorRef.current?.offsetWidth };
    const searchable = _searchable && (size === 'large' || size === 'medium');
    const dataQa = triggerProps['data-qa'] || 'magritte-select-activator';

    // селект без поиска (переключаем фокус и отправляем blur)
    useLayoutEffect(() => {
        if (searchable && !isMobile) {
            return;
        }
        if (!showOptions && prevVisibleRef.current) {
            setInitialValuesWithoutOnChange();
            if (forcedState === 'focus') {
                activatorRef.current?.focus();
                setForcedState?.(null);
                showedByFocus.current = false;
            }
        }
        if (!forcedState && !showOptions && prevVisibleRef.current) {
            onBlur?.();
        }
        prevVisibleRef.current = showOptions;
    }, [activatorRef, forcedState, isMobile, onBlur, searchable, setInitialValuesWithoutOnChange, showOptions]);

    // селект с поиском (нужно подменить контрол)
    useLayoutEffect(() => {
        if (!searchable || isMobile) {
            return;
        }
        if (forcedState === 'focus' || forcedState === 'focusAndSelect') {
            setSearchInputState({
                useSearchInput: useDefaultTrigger,
                emitEvents: true,
            });
        }
    }, [forcedState, isMobile, searchable, useDefaultTrigger]);

    // триггер подменяться на input (тут пачка логики + blur)
    useLayoutEffect(() => {
        if (!searchInputState.emitEvents) {
            return;
        }
        // немного магипуляций, если тригер дефотный
        if (searchInputState.useSearchInput) {
            if (!inputRef.current) {
                return;
            }
            if (forcedState === 'focus') {
                inputRef.current.focus();
            }
            // например во время выбора опции, нужно выбрать текст
            if (forcedState === 'focusAndSelect') {
                inputRef.current?.focus();
                inputRef.current?.select();
            }
        }
        if (!showOptions && prevVisibleRef.current) {
            setInitialValuesWithoutOnChange();
        }
        if (!forcedState && !showOptions) {
            setSearchInputState({
                useSearchInput: false,
                emitEvents: false,
            });
            if (prevVisibleRef.current) {
                onBlur?.();
            }
        }
        prevVisibleRef.current = showOptions;
    }, [forcedState, onBlur, setForcedState, searchInputState, showOptions, setInitialValuesWithoutOnChange]);

    const handleKeyDown = useCallback<KeyboardEventHandler>(
        (event) => {
            if (showOptions && keyboardMatch(event.nativeEvent, keyboardKeys.Escape)) {
                showedByFocus.current = false;
                setForcedState('focus');

                toggleListVisibility();
                return;
            }

            // движение по списку опций
            if (showOptions && keyboardMatch(event.nativeEvent, keyboardKeys.ArrowUp)) {
                focusOptionsList(event, dropRef?.current, 'up');
                return;
            }

            if (showOptions && keyboardMatch(event.nativeEvent, keyboardKeys.ArrowDown)) {
                focusOptionsList(event, dropRef?.current, 'down');
                return;
            }

            // после закрытия пользователь решил что-то поделать опять
            if (
                !showOptions &&
                !keyboardMatches(event.nativeEvent, [keyboardKeys.Tab, keyboardKeys.Escape, keyboardKeys.Space])
            ) {
                toggleListVisibility();
            }
        },
        [showOptions, toggleListVisibility]
    );

    return (
        <>
            {!searchInputState.useSearchInput &&
                renderTrigger({
                    values: selectedLabels,
                    expanded: showOptions,
                    toggleExpanded: toggleListVisibility,
                    onChange: () => {
                        if (disabled) {
                            return;
                        }
                        // открываем по фокусу, закрываем по клику
                        // повторно открываем по клику (события фокуса уже не будет)
                        if (showedByFocus.current) {
                            showedByFocus.current = false;
                            return;
                        }
                        toggleListVisibility();
                    },
                    onClear: () => clearSelectedValues(),
                    ref: activatorRef,
                    onKeyDown: handleKeyDown,
                    onBlur: () => {
                        setForcedState(null);
                        if (showOptions && optionList.length === 0 && !emptyOptionsHint) {
                            toggleListVisibility();
                        }
                        if (!showOptions && forcedState === null) {
                            onBlur?.();
                        }
                    },
                    onFocus: () => {
                        if (disabled || showOptions) {
                            return;
                        }
                        if (!showOptions && forcedState !== 'focus' && forcedState !== 'focusAndSelect') {
                            onFocus?.();
                            setForcedState('focus');

                            toggleListVisibility();
                        }
                        showedByFocus.current = true;
                    },
                    'data-qa': dataQa,
                    setSearchValue,
                    optionsCount: optionList.length,
                    ...triggerProps,
                } as TriggerPropsWithRef & SetSearchValue & OptionsCount)}
            {searchInputState.useSearchInput && (
                <div style={inputWrapperStyles}>
                    <BaseInput
                        {...sizeProp}
                        ref={inputRef}
                        wrapperRef={activatorRef}
                        value={searchValue}
                        onChange={setSearchValue}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                            setForcedState('focus');
                        }}
                        onBlur={() => {
                            setForcedState(null);
                            if (showOptions && optionList.length === 0 && !emptyOptionsHint) {
                                toggleListVisibility();
                            }
                        }}
                        data-qa="magritte-select-activator-input"
                        placeholder={selectedLabels.length > 0 ? selectedLabels.join(', ') : triggerLabel}
                        autoComplete="off"
                    />
                </div>
            )}
            {isMobile && (
                <SelectBottomSheet
                    applyChangesButton={applyChangesButton}
                    clearButton={clearButton}
                    clearSelectedValues={clearSelectedValues}
                    error={error}
                    height={searchable || Boolean(error) || loading ? 'full-screen' : bottomSheetHeight}
                    visible={Boolean(showOptions || internalLoading)}
                    value={values}
                    isLimitExceeded={isLimitExceeded}
                    name={name}
                    multiple={multiple}
                    type={type}
                    loading={loading || internalLoading}
                    searchable={_searchable}
                    useInitialValuesOnClearAction={useInitialValuesOnClearAction}
                    initialValuesOnOpening={initialValuesOnOpening}
                    renderItem={renderMobileItem}
                    renderAlertOnExitWithoutSaving={renderAlertOnExitWithoutSaving}
                    renderButtonsContainer={renderButtonsContainer}
                    renderHeader={renderHeader}
                    headerTitle={_headerTitle || triggerLabel}
                    optionList={optionList}
                    optionsListRef={optionsListRefCallback}
                    onChange={onChange}
                    onClose={onOptionsListClose}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    setInitialValues={setInitialValues}
                    setSelectedValues={setSelectedValues}
                    onBottomSheetClose={onBottomSheetClose}
                    onBottomSheetOpen={onBottomSheetOpen}
                    onSearchSubmit={onSearchSubmit}
                    onApply={onApply}
                    onClear={onClear}
                    emptyOptionsHint={emptyOptionsHint}
                    emptyOptionsSearchHint={emptyOptionsSearchHint}
                    searchInProgress={searchInProgress}
                    internalError={internalError}
                    reload={reload}
                    innerErrorTrls={innerErrorTrls}
                    optionDataQa={optionDataQa}
                />
            )}
            {!isMobile && (
                <SelectDrop
                    activatorRef={activatorRef}
                    applyChangesButton={applyChangesButton}
                    clearButton={clearButton}
                    clearSelectedValues={clearSelectedValues}
                    isLimitExceeded={isLimitExceeded}
                    name={name}
                    multiple={multiple}
                    maxWidth={maxWidth}
                    type={type}
                    loading={loading || internalLoading}
                    useInitialValuesOnClearAction={useInitialValuesOnClearAction}
                    onClose={onOptionsListClose}
                    renderItem={renderDesktopItem}
                    renderButtonsContainer={renderButtonsContainer}
                    plain={plain}
                    optionList={optionList}
                    optionsListRef={optionsListRefCallback}
                    value={values}
                    onChange={onChange}
                    searchValue={searchValue}
                    searchable={searchable}
                    forceDropVisibleWithEmptyOptions={forceDropVisibleWithEmptyOptions || internalLoading}
                    setSearchValue={setSearchValue}
                    setInitialValues={setInitialValues}
                    visible={!error && (showOptions || internalLoading)}
                    widthEqualToActivator={widthEqualToActivator}
                    renderContentBefore={renderContentBefore}
                    setForcedState={setForcedState}
                    setSelectedValues={setSelectedValues}
                    maxHeight={maxHeight}
                    onDropOpen={onDropOpen}
                    onDropClose={onDropClose}
                    onApply={onApply}
                    onClear={onClear}
                    emptyOptionsHint={emptyOptionsHint}
                    emptyOptionsSearchHint={emptyOptionsSearchHint}
                    searchInProgress={searchInProgress}
                    internalError={internalError}
                    reload={reload}
                    innerErrorTrls={innerErrorTrls}
                    optionDataQa={optionDataQa}
                />
            )}
        </>
    );
};
